/**
 * Klaviyo Signup Service
 * Client Subscription API handler
 */

class KlaviyoSignup {
  constructor() {
    this.config = window.klaviyoSignupConfig || {};
  }

  /**
   * Submit form to Klaviyo API
   * @param {HTMLFormElement} formElement
   * @param {Object} options - Override defaults (form_type, list_id, etc)
   */
  async submit(formElement, options = {}) {
    const formData = new FormData(formElement);

    const email = formData.get('email') || formData.get('ue') || formData.get('user_email');
    const firstName = formData.get('firstname') || formData.get('first_name') || '';
    const lastName = formData.get('lastname') || formData.get('last_name') || '';
    let phone = '';

    // Prioritize intlTelInput for full international E.164 format
    const telInput = formElement.querySelector('[data-tel-input]');
    if (telInput?.intlTelInput) {
      // Use getNumber() which includes the country code (e.g. +44)
      phone = telInput.intlTelInput.getNumber();
    }

    // Fallback to standard form fields if no intl input was found or it was empty
    if (!phone) {
      phone =
        formData.get('phone_number') ||
        formData.get('phone') ||
        formData.get('full_phone') ||
        formData.get('phone_mobile') ||
        '';
    }

    // Fallback to email input value
    if (!email) {
      const emailInput = formElement.querySelector('[type="email"]');
      if (emailInput) email = emailInput.value;
    }

    if (!email) {
      throw { error: 'Email is required' };
    }

    const apiKey = options.api_key || this.config.apiKey;
    const formType = options.form_type;
    let listId = options.list_id || this.config.lists?.[formType?.toLowerCase()];

    // Final fallback to generic subscribers list if still missing
    if (!listId) {
      listId = this.config.lists?.subscribers;
    }

    if (!apiKey || !listId) {
      console.error('Klaviyo config missing:', { apiKey, listId, formType });
      throw { error: 'Klaviyo not configured' };
    }

    const hasPhone = !!(phone && phone.length > 0);
    const properties = {};

    if (this.config.storeSignup) {
      properties.store_signup = this.config.storeSignup;
    }

    if (formType) {
      properties.form_type = formType;
    }

    // Build subscriptions object based on provided channels
    // Klaviyo requires at least one of 'transactional' or 'marketing' per channel
    const subscriptions = {
      email: {
        marketing: {
          consent: 'SUBSCRIBED',
          consented_at: new Date().toISOString(),
        },
      },
    };

    if (hasPhone) {
      subscriptions.sms = {
        marketing: {
          consent: 'SUBSCRIBED',
          consented_at: new Date().toISOString(),
        },
      };
    }

    const attributes = {
      profile: {
        data: {
          type: 'profile',
          attributes: {
            email: email,
            phone_number: hasPhone ? phone : null,
            first_name: firstName,
            last_name: lastName,
            properties: properties,
            subscriptions: subscriptions,
          },
        },
      },
    };

    if (formType) {
      attributes.custom_source = formType;
    }

    const payload = {
      data: {
        type: 'subscription',
        attributes: attributes,
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };

    const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: this.config.revision || '2026-04-15',
      },
      body: JSON.stringify(payload),
    });

    const responseText = await response.text();
    const responseData = responseText ? JSON.parse(responseText) : {};

    if (!response.ok) {
      throw { error: responseData?.errors?.[0]?.detail || 'Something went wrong. Please try again.' };
    }

    return responseData;
  }
}

// Initialize global service and notify listeners
document.addEventListener('DOMContentLoaded', () => {
  window.klaviyoSignup = new KlaviyoSignup();
  window.dispatchEvent(new Event('klaviyoSignup:initialized'));
});
